import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import STRINGS from './strings';

const LanguageContext = createContext(null);
const STORAGE_KEY = 'dashboardLang';

const getInitialLang = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'zh') return saved;
  } catch {}
  return 'en';
};

// Resolve a dot-path key (e.g. "orders.newOrders") against the active dictionary,
// falling back to English, then to the key itself so nothing ever renders blank.
function resolve(dict, key) {
  return key.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), dict);
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(getInitialLang);

  const setLanguage = useCallback((next) => {
    if (next !== 'en' && next !== 'zh') return;
    setLang(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  }, []);

  const toggle = useCallback(() => {
    setLang(prev => {
      const next = prev === 'en' ? 'zh' : 'en';
      try { localStorage.setItem(STORAGE_KEY, next); } catch {}
      return next;
    });
  }, []);

  const t = useCallback((key, fallback) => {
    const val = resolve(STRINGS[lang], key);
    if (val !== undefined) return val;
    const en = resolve(STRINGS.en, key);
    if (en !== undefined) return en;
    return fallback !== undefined ? fallback : key;
  }, [lang]);

  const value = useMemo(() => ({ lang, t, toggle, setLanguage }), [lang, t, toggle, setLanguage]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Safe fallback if used outside the provider — returns English passthrough.
    return { lang: 'en', t: (k, f) => f !== undefined ? f : k, toggle: () => {}, setLanguage: () => {} };
  }
  return ctx;
}
